var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx } from "react/jsx-runtime";
import * as React from 'react';
import { JssContext, JssProvider, ThemeProvider } from 'react-jss';
import { create } from 'jss';
import camelCase from 'jss-plugin-camel-case';
import defaultUnit from 'jss-plugin-default-unit';
import global from 'jss-plugin-global';
import nested from 'jss-plugin-nested';
import propsSort from 'jss-plugin-props-sort';
import functions from 'jss-plugin-rule-value-function';
import vendorPrefixer from 'jss-plugin-vendor-prefixer';
import { createHawkinsThemeObject } from './utils';
var INSERTION_COMMENT_NODE_COMMENT = '__--BEGIN_HAWKINS_CSS_INJECTION--__';
var jss = null;
/**
 * Utility function that sets up a single instance of JSS for the browser,
 * but also ensures that all future styles inject at he correct point, and that
 * the injection point comment exists only once
 */
function setupJSS(insertionPoint) {
    if (typeof document === 'undefined' || !Boolean(document.head))
        return null;
    if (!jss) {
        var insertionPointSingleton = insertionPoint !== null && insertionPoint !== void 0 ? insertionPoint : document.createComment(INSERTION_COMMENT_NODE_COMMENT);
        var firstHeadChild = document.head.children[0];
        // Check if the insertion point is already mounted in the DOM
        if (!insertionPointSingleton.parentNode) {
            // No children, so just put the comment node wherever
            if (!firstHeadChild)
                document.head.appendChild(insertionPointSingleton);
            // <head /> isn't empty, so we'll put our comment node in at the top
            else
                document.head.insertBefore(insertionPointSingleton, firstHeadChild);
        }
        jss = createJSS({ insertionPoint: insertionPointSingleton });
    }
    return jss;
}
/**
 * Create a JSS instance with the Hawkins defaults
 *
 * Useful for SSR/hydration scenarios
 **/
export function createJSS(options) {
    if (options === void 0) { options = {}; }
    return create(__assign({ plugins: [
            functions(),
            global(),
            nested(),
            camelCase(),
            defaultUnit(),
            propsSort(),
            typeof window !== 'undefined' && vendorPrefixer(),
        ].filter(Boolean) }, options));
}
/**
 * Provides the Hawkins theme object to all Hawkins components that render underneath it.
 *
 * This provider is automatically included with the HawkinsProvider from `@hawkins/components`.
 *
 * You should never need to include this component directly in your application.
 */
export var HawkinsJSSProvider = function (props) {
    var _a;
    // props
    var children = props.children, externalTheme = props.externalTheme, generateClassName = props.generateClassName, insertionPoint = props.insertionPoint, _b = props.theme, theme = _b === void 0 ? 'light' : _b;
    // memos
    var themeObject = React.useMemo(function () {
        return createHawkinsThemeObject(theme, externalTheme);
    }, [theme, externalTheme]);
    // set up JSS if necessary; if it's defined above us, assume people know what they are doing
    var outerContext = React.useContext(JssContext);
    var jss = (_a = outerContext.jss) !== null && _a !== void 0 ? _a : setupJSS(insertionPoint);
    if (!jss) {
        console.error('You must provide JssContext.jss when doing server-side rendering');
        return null;
    }
    return (_jsx(JssProvider, __assign({ generateId: generateClassName, jss: jss }, { children: _jsx(ThemeProvider, __assign({ theme: themeObject }, { children: children })) })));
};
HawkinsJSSProvider.displayName = 'HawkinsJSSProvider';
